




































































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import CategoryBarRow from "./CategoryBarRow.vue";
import CountColumnHeader from "./CountColumnHeader.vue";
import GenAiCard from "./GenAiCard.vue";
import GenAiDonut from "./GenAiDonut.vue";
import LevelSlider from "./LevelSlider.vue";
import ViewModeToggle from "./ViewModeToggle.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { LOAD_FAILED_TEXT, state } from "../data/state";
import { STOP_TEXT, use_visibility_stop } from "../data/stops";
import {
  CategoryCount,
  INFO_CATEGORIES,
  SENSITIVITY_STOPS,
  activities_for,
} from "../data/model";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";
import { show_gen_ai_breakdown, show_gen_ai_events } from "../data/drilldown";
import { visibility_share_line } from "../data/share";
import { use_view_mode } from "../data/view-mode";



/** the sensitivity slider's active stops as the numeric levels rows carry */
const SENS_BY_TONE: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

/**
 * Widget 3 — "Information Shared With AI" (gen-ai-ui, Sep 2026).
 *
 * Ami's spec: `AI_relation (2 specific) || virtual_label (on content item)`,
 * "same as #2 but no severity". The two relations are the employee -> AI pair,
 * Shared and Uploaded — the prototype's own USER_TO_AI set — and the category
 * comes from the content item's label.
 *
 * This is a SENSITIVITY slider, not a risk one: unclassified content is a real
 * bucket here, so the widest stop is "All" and includes it. Each stop is its
 * own query (data/stops.ts) and comes back as one count per category, so a
 * bar is one segment.
 */
export default Vue.extend({
  components: {
    CategoryBarRow,
    CountColumnHeader,
    GenAiCard,
    GenAiDonut,
    LevelSlider,
    ViewModeToggle,
    SkeletonLoaderItem,
  },
  setup() {
    const palette = useGenAiPalette();
    const view = use_view_mode("informationAccessed");

    const is_loading = computed(() => state.loading.value.is_loading);

    /** opens on "All", so the card shows the full picture first */
    const level = ref(SENSITIVITY_STOPS.length);
    const active_keys = computed(() => SENSITIVITY_STOPS.slice(0, level.value));
    const at = use_visibility_stop(level);

    /** what employees shared to AI in a category, at a stop's counts */
    const shared_in = (counts: any, id: CategoryCount["id"]) =>
      counts
        ? activities_for("employee").reduce((sum, a) => sum + counts[id][a.id], 0)
        : 0;

    const items = computed<CategoryCount[]>(() =>
      INFO_CATEGORIES.map((c) => ({ ...c, count: shared_in(at.data.value, c.id) }))
    );

    const total_for = (item: CategoryCount) => item.count;

    const headline = computed(() =>
      items.value.reduce((sum, item) => sum + total_for(item), 0)
    );

    /**
     * Scaled against the widest row's FULL inventory ("All"), not the current
     * stop — gen-ai-ui does it this way on this card only, so narrowing the
     * ruler shows how much of each category it has left out.
     */
    const max_value = computed(() => {
      const all = state.visibility.value.data.all;
      return Math.max(
        1,
        ...INFO_CATEGORIES.map((c) => shared_in(all, c.id)),
        ...items.value.map((d) => d.count)
      );
    });

    /** one segment: a stop's count is not split by sensitivity */
    const segments_for = (item: CategoryCount) => [
      {
        key: at.stop.value,
        value: item.count,
        color:
          at.stop.value === "all"
            ? palette.value.categoryChart[item.id]
            : palette.value.sensitivity[at.stop.value].bar,
        title: `${STOP_TEXT[at.stop.value]}: ${item.count.toLocaleString()}`,
      },
    ];

    /** a failed request has no count: a dash, never a zero */
    const load_failed = computed(() => state.failed.value.labels || at.failed.value);
    const stop_loading = computed(() => !is_loading.value && at.loading.value);

    const subtitle = computed(() =>
      visibility_share_line(state.overview.value.shares, "employee", headline.value)
    );

    const aria_for = (item: CategoryCount) =>
      `Open ${total_for(item)} ${item.name} items shared with AI, ` +
      `${STOP_TEXT[at.stop.value].toLowerCase()}.`;

    const donut_items = computed(() =>
      items.value.map((item) => ({
        label: item.name,
        value: total_for(item),
        color: palette.value.categoryChart[item.id],
      }))
    );

    // the app's "What is your information?" view, scoped to what employees
    // shared with AI
    const show_breakdown = async () => {
      // the sub-labels are asked for the first time the expand opens
      await state.load_info_breakdown();
      show_gen_ai_breakdown(
        "<b>What</b> information did employees share with AI?",
        state.overview.value.info_breakdown
      );
    };

    // the list inherits the slider: clicking 33 opens 33 rows, not all 200
    const show_events = (item?: CategoryCount) =>
      item &&
      show_gen_ai_events(
        "<b>" + item.name + "</b>: Information Shared with AI",
        {
          widget: "accessed",
          category: item.id,
          // employee -> AI only, the same population the row counts
          direction: "employee",
          sensitivities: active_keys.value.map((k) => SENS_BY_TONE[k]),
        }
      );

    return {
      view,
      items,
      is_loading,
      stop_loading,
      level,
      headline,
      load_failed,
      LOAD_FAILED_TEXT,
      subtitle,
      total_for,
      segments_for,
      max_value,
      aria_for,
      donut_items,
      show_breakdown,
      show_events,
      // gen-ai-ui SensitivityRuler: classifications, not risk levels
      stops: [
        { label: "High", pill: "High Sensitivity", key: "high" },
        { label: "+ Medium", pill: "High + Medium", key: "medium" },
        { label: "+ Low", pill: "High + Med + Low", key: "low" },
        { label: "All", pill: "Any Content", key: "none" },
      ],
      T: palette,
      TY: TYPE,
      S: SIZE,
    };
  },
});
